import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  useTheme,
  useMediaQuery,
  Grid,
  Paper,
  Button,
  Chip,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Favorite,
  Share,
  Bookmark,
  LocationOn,
  CalendarToday,
  Person,
  AttachMoney,
  TrendingUp,
} from "@mui/icons-material";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import CampaignHeader from "../../components/common/CampaignDetailPageComponents/CampaignHeader";
import CampaignProgress from "../../components/common/CampaignDetailPageComponents/CampaignProgress";
import CampaignSidebar from "../../components/common/CampaignDetailPageComponents/CampaignSidebar";
import CampaignTopBar from "../../components/common/CampaignDetailPageComponents/CampaignTopBar";
import HowToDonate from "../../components/common/CampaignDetailPageComponents/HowToDonate";
import Projects from "../../components/common/CampaignDetailPageComponents/Projects";
import CampaignProduct from "../../components/common/CampaignDetailPageComponents/CampaignProduct";
import Updates from "../../components/common/CampaignDetailPageComponents/Updates";
import Comments from "../../components/common/CampaignDetailPageComponents/Comments";
import Donors from "../../components/common/CampaignDetailPageComponents/Donors";
import ReferFriend from "../../components/common/CampaignDetailPageComponents/ReferFriend";
import CampaignPopup from "../../components/common/CampaignDetailPageComponents/CampaignPopup";
import { getCampaignBySlug } from "../../api/services/campaignService";
import RecentCampaigns from "../../components/common/CampaignDetailPageComponents/RecentCampaigns";
import Faq from "../../components/common/CampaignDetailPageComponents/Faq";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import DonationBox from "../../components/common/DonationBox";

const CampaignDetailPage = ({ campaign: propCampaign }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const { slug } = useParams(); // Get campaign slug from URL
  const location = useLocation();

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [campaign, setCampaign] = useState(propCampaign || null);
  const [loading, setLoading] = useState(!propCampaign);
  const [error, setError] = useState(null);
  // Amount from donation box so top "Donate Now" can pass it
  const [donationAmount, setDonationAmount] = useState(3000);

  useEffect(() => {
    if (sessionStorage.getItem("returningFromCheckout")) {
      sessionStorage.removeItem("returningFromCheckout");
    }
  }, []);

  // Update campaign if prop changes
  useEffect(() => {
    if (propCampaign) {
      setCampaign(propCampaign);
      setLoading(false);
      setError(null);
    }
  }, [propCampaign]);

  // Fetch campaign data from API only if not provided as prop
  useEffect(() => {
    if (propCampaign) {
      // Campaign provided as prop, don't fetch
      return;
    }

    const fetchCampaign = async () => {
      try {
        setLoading(true);
        const response = await getCampaignBySlug(slug, location.pathname.startsWith("/story/"));
        // Service returns { data: {...} }
        if (response && response.data) {
          setCampaign(response.data);
        } else {
          setCampaign(null);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCampaign();
    }
  }, [slug, propCampaign, location.pathname]);

  // Live raised amount via SSE — updates progress bar in real-time when QR donations arrive
  useEffect(() => {
    if (!campaign?.id) return;
    const base = import.meta.env.VITE_API_BASE_URL || '';
    const es = new EventSource(`${base}/campaigns/${campaign.id}/live`);
    es.onmessage = (e) => {
      try {
        const { raised } = JSON.parse(e.data);
        if (typeof raised === 'number') {
          setCampaign(prev => prev ? { ...prev, raisedAmount: raised } : prev);
        }
      } catch (_) {}
    };
    return () => es.close();
  }, [campaign?.id]);

  // Show popup after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 3_000);

    return () => clearTimeout(timer);
  }, []);

  // Handle popup close
  const handleClosePopup = () => {
    setShowPopup(false);
  };

  // Handle sidebar/topbar navigation
  const handleSidebarNavigation = (sectionId) => {
    console.log("🟢 handleSidebarNavigation called with sectionId:", sectionId);
    
    // Function to find the visible element (handles duplicate IDs for desktop/mobile)
    const findVisibleElement = (id) => {
      // Get all elements with this ID (there might be duplicates for desktop/mobile)
      const allElements = Array.from(document.querySelectorAll(`#${id}`));
      
      // Find the visible one (not hidden by CSS)
      const visibleElement = allElements.find(el => {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          rect.width > 0 &&
          rect.height > 0
        );
      });
      
      // If no visible element found, return the first one (fallback)
      return visibleElement || allElements[0] || document.getElementById(id);
    };
    
    // Use multiple attempts to ensure element is ready
    const attemptScroll = (attempt = 0) => {
      const maxAttempts = 5;
      const delay = attempt * 50; // Increasing delay: 0ms, 50ms, 100ms, 150ms, 200ms
      
      setTimeout(() => {
        const element = findVisibleElement(sectionId);
        console.log(`🟢 Attempt ${attempt + 1}: Element found:`, element ? "YES" : "NO", "for id:", sectionId);
        
        if (element) {
          // Get current scroll position
          const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
          
          // Get the element's bounding rect (relative to viewport)
          const rect = element.getBoundingClientRect();
          
          // Check if element has valid dimensions
          if (rect.width === 0 && rect.height === 0 && attempt < maxAttempts - 1) {
            console.log("🟢 Element has no dimensions, retrying...");
            attemptScroll(attempt + 1);
            return;
          }
          
          // Calculate offset (accounting for any fixed headers or spacing)
          const offset = isMobile ? 100 : 120;
          
          // Calculate target scroll position
          // rect.top is relative to viewport, add current scroll to get absolute position
          const elementTop = rect.top + scrollTop;
          const targetScrollPosition = elementTop - offset;
          
          // Check if we're already close to the target (within 20px tolerance)
          const currentDistance = Math.abs(scrollTop - targetScrollPosition);
          const isAlreadyAtTarget = currentDistance < 20;
          
          console.log("🟢 Scroll Debug Info:", {
            sectionId,
            attempt: attempt + 1,
            elementTop: elementTop.toFixed(2),
            currentScrollTop: scrollTop.toFixed(2),
            rectTop: rect.top.toFixed(2),
            rectBottom: rect.bottom.toFixed(2),
            rectWidth: rect.width.toFixed(2),
            rectHeight: rect.height.toFixed(2),
            offset,
            targetScrollPosition: targetScrollPosition.toFixed(2),
            currentDistance: currentDistance.toFixed(2),
            isAlreadyAtTarget,
            isMobile,
            windowInnerHeight: window.innerHeight,
            elementVisible: element.offsetParent !== null,
            computedDisplay: window.getComputedStyle(element).display,
          });
          
          // Only proceed if element has valid position or we've exhausted attempts
          if ((rect.top === 0 && rect.bottom === 0 && rect.height === 0) && attempt < maxAttempts - 1) {
            console.log("🟢 Element position is 0, retrying...");
            attemptScroll(attempt + 1);
            return;
          }
          
          if (isAlreadyAtTarget) {
            console.log("🟢 Already at target position, skipping scroll");
            return;
          }
          
          // Ensure we don't scroll to negative position
          const finalPosition = Math.max(0, targetScrollPosition);
          
          console.log("🟢 Scrolling to position:", finalPosition, "(current:", scrollTop.toFixed(2), ")");
          
          window.scrollTo({
            top: finalPosition,
            behavior: "smooth",
          });
        } else {
          if (attempt < maxAttempts - 1) {
            console.log("🟢 Element not found, retrying...");
            attemptScroll(attempt + 1);
          } else {
            console.error("❌ Element not found after", maxAttempts, "attempts for id:", sectionId);
            // Log all available IDs on the page for debugging
            const allElementsWithIds = Array.from(document.querySelectorAll('[id]')).map(el => ({
              id: el.id,
              tagName: el.tagName,
              visible: el.offsetParent !== null,
              display: window.getComputedStyle(el).display,
              rect: {
                top: el.getBoundingClientRect().top.toFixed(2),
                height: el.getBoundingClientRect().height.toFixed(2),
              }
            }));
            console.log("🟢 Available elements with IDs:", allElementsWithIds);
          }
        }
      }, delay);
    };
    
    // Start the scroll attempt
    requestAnimationFrame(() => {
      attemptScroll(0);
    });
  };

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If campaign not found or error, redirect to campaigns page
  if (error || !campaign) {
    return <Navigate to="/campaigns" replace />;
  }

  // Breadcrumb items for campaign detail page
  const breadcrumbItems = [
    { label: "Campaigns", path: "/campaigns" },
    { label: "Campaign Detail", path: null }, // Current page - not clickable
  ];

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />
      <Box
        sx={{
          width: "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          // gap: "30px",
        }}
      >
        {/* Campaign Header */}
        <CampaignHeader campaignData={campaign} donationAmount={donationAmount} />

        {/* Campaign Progress */}
        {campaign && (Number(campaign.hideAll) || 0) !== 1 && (
          <Box
            sx={{
              width: isMobile ? "90%" : "97%",
              margin: "0 auto",
              marginTop: isMobile ? "-60px" : "-105px",
              marginBottom: isMobile ? "16px" : 0,
              zIndex: 1,
              position: "relative",
            }}
          >
            <CampaignProgress campaignData={campaign} />
          </Box>
        )}

        {/* Top Bar for mobile/tablet (1000px and below) */}
        <Box
          sx={{
            display: { xs: "block", "@media (min-width: 1001px)": { display: "none" } },
            marginTop: isMobile ? "0px" : "30px",
            marginBottom: isMobile ? "20px" : "30px",
            position: "sticky",
            top: "10px", // Leave gap above the top bar
            zIndex: 999,
            backgroundColor: "transparent",
            paddingTop: isMobile ? "0px" : "0px",
            paddingBottom: isMobile ? "0px" : "0px",
          }}
        >
          <CampaignTopBar onNavigate={handleSidebarNavigation} campaignData={campaign} />
        </Box>

        {/* Two Section Layout - Desktop only (above 1100px) */}
        <Box
          sx={{
            display: { xs: "none", "@media (min-width: 1001px)": { display: "flex" } },
            flexDirection: "row",
            gap: "30px",
            marginTop: "50px",
            position: "relative",
            marginBottom: "60px",
          }}
        >
          {/* Left Section - Desktop only */}
          <Box
            sx={{
              width: "23.41%",
              position: "sticky",
              top: "20px",
              alignSelf: "flex-start",
            }}
          >
            <CampaignSidebar onNavigate={handleSidebarNavigation} campaignData={campaign} />
            <Box sx={{ marginTop: "20px" }}>
              <HowToDonate />
            </Box>
          </Box>

          {/* Right Section - Desktop only */}
          <Box
            sx={{
              width: "76.59%",
            }}
          >
            {/* Products Section - Only show if products exist */}
            {campaign?.products && Array.isArray(campaign.products) && campaign.products.length > 0 && (
              <Box id="products" sx={{ marginTop: "0px", }}>
                <CampaignProduct campaignData={campaign} />
              </Box>
            )}
            
            {/* Projects Section - Only show if projects exist */}
            {campaign?.projects && Array.isArray(campaign.projects) && campaign.projects.length > 0 && (
              <Box id="project" sx={{ marginTop: "30px", marginBottom: "30px" }}>
                <Projects campaignData={campaign} />
              </Box>
            )}

            {/* Updates Section - Only show if updates exist */}
            {campaign?.updates && Array.isArray(campaign.updates) && campaign.updates.length > 0 && (
              <Box id="updates">
                <Updates campaignData={campaign} />
              </Box>
            )}

            {/* Comments Section */}
            <Box id="comments" sx={{ marginTop: "30px", marginBottom: "30px" }}>
              <Comments campaignData={campaign} />
            </Box>

            {/* Donors Section */}
            <Box sx={{ marginBottom: "30px" }}>
              <Donors campaignData={campaign} />
            </Box>
          </Box>
        </Box>

        {/* Full Width Content - Mobile/Tablet only (1100px and below) */}
        <Box
          sx={{
            display: { xs: "block", "@media (min-width: 1001px)": { display: "none" } },
            width: "100%",
            marginTop: "0px",
            marginBottom: isMobile ? "0px" : "60px",
          }}
        >
          {/* Products Section - Only show if products exist */}
          {campaign?.products && Array.isArray(campaign.products) && campaign.products.length > 0 && (
            <Box id="products" sx={{ marginTop: "0px", marginBottom: "30px" }}>
              <CampaignProduct campaignData={campaign} />
            </Box>
          )}
          
          {/* Projects Section - Only show if projects exist */}
          {campaign?.projects && Array.isArray(campaign.projects) && campaign.projects.length > 0 && (
            <Box id="project">
              <Projects campaignData={campaign} />
            </Box>
          )}

          {/* Updates Section - Only show if updates exist */}
          {campaign?.updates && Array.isArray(campaign.updates) && campaign.updates.length > 0 && (
            <Box id="updates" sx={{ marginTop: "30px", marginBottom: "30px" }}>
              <Updates campaignData={campaign} />
            </Box>
          )}

          {/* Comments Section */}
          <Box id="comments" sx={{ marginBottom: "30px" }}>
            <Comments campaignData={campaign} />
          </Box>

          {/* Donors Section */}
          <Box sx={{ marginBottom: "30px" }}>
            <Donors campaignData={campaign} />
          </Box>
        </Box>

        {/* Refer Friend Section */}
        <Box sx={{ marginBottom: isMobile ? "30px" : "15px" }}>
          <ReferFriend campaignData={campaign} />
        </Box>

        {/* Recent Campaigns Section - Hidden on mobile */}
        {!isMobile && (
          <Box sx={{ marginBottom: "15px" }}>
            <RecentCampaigns campaignData={campaign} />
          </Box>
        )}

        {/* Faq Section */}
        <Box sx={{ marginBottom: isMobile ? "0px" : "60px" }}>
          <Faq />
        </Box>
      </Box>

      {/* News Logo Section */}
      <Box sx={{ marginBottom: isMobile ? "0px" : "0px" }}>
        <NewsLogoSection />
      </Box>

      {/* How to Donate Section - Mobile only */}
      {isMobile && (
        <Box sx={{ marginBottom: "0px" }}>
          <HowToDonate />
        </Box>
      )}

      {/* Campaign Popup */}
      <CampaignPopup
        isOpen={showPopup}
        onClose={handleClosePopup}
        campaignData={campaign}
      />

      {/* Sticky Donation Box */}
      <Box
        data-donation-box="true"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: isMobile ? "0px" : "10px 0",
        }}
      >
          <DonationBox campaignTitle={campaign?.title} campaignId={campaign?.id} onDonationAmountChange={setDonationAmount} />
      </Box>
    </>
  );
};

export default CampaignDetailPage;
